/**
 * examBank.js — reserve of 30 MCQs; each attempt draws 8 at random.
 *
 * Every question carries `lab` parameters so a wrong answer can be
 * EXPERIENCED in the Physics Lab (the wrong value is driven and fails).
 * Lab model (flat circle): v_max = √(μ·g·r);  vertical loop: v_top ≥ √(g·r).
 */

export const EXAM_QUESTIONS = [
  // ── Computing v_max ────────────────────────────────────────────
  {
    id: "Q1", topic: "Computing v_max",
    question: "A flat curve has r = 40 m and μ = 0.4 (g = 10 m/s²). Maximum safe speed?",
    options: [
      { text: "12.6 m/s", correct: true, why: "v_max = √(μ·g·r) = √160 ≈ 12.6 m/s." },
      { text: "160 m/s", correct: false, why: "160 is v² — you forgot the square root." },
      { text: "16 m/s", correct: false, why: "16 = μ·r — ignores g and the root." },
      { text: "4 m/s", correct: false, why: "4 = μ·g, an acceleration, not a speed." },
    ],
    lab: { r: 40, mu: 0.4, vCorrect: 12.6, vWrongDefault: 16 },
    wrongSpeeds: { "160 m/s": 30, "16 m/s": 16, "4 m/s": 4 },
  },
  {
    id: "Q2", topic: "Computing v_max",
    question: "r = 90 m, μ = 0.9. What is v_max?",
    options: [
      { text: "≈ 28.5 m/s", correct: true, why: "√(0.9·10·90) = √810 ≈ 28.5 m/s." },
      { text: "≈ 81 m/s", correct: false, why: "81 = μ·g·r/10 — and no square root taken." },
      { text: "≈ 9 m/s", correct: false, why: "9 = μ·g — that's the available acceleration." },
      { text: "≈ 900 m/s", correct: false, why: "900 = v². Take the root: ≈ 28.5 m/s." },
    ],
    lab: { r: 90, mu: 0.9, vCorrect: 28.5, vWrongDefault: 40 },
    wrongSpeeds: {},
  },
  {
    id: "Q3", topic: "Computing v_max",
    question: "A curve allows 20 m/s when μ = 0.5. A sudden oil patch drops μ to 0.125 (¼). New v_max?",
    options: [
      { text: "10 m/s — half", correct: true, why: "v_max ∝ √μ: quartering μ halves v_max (√¼ = ½)." },
      { text: "5 m/s — a quarter", correct: false, why: "μ is under the root: ¼ μ → ½ v_max, i.e. 10 m/s." },
      { text: "20 m/s — unchanged", correct: false, why: "On a flat curve friction is the only grip: v_max = √(μ·g·r) depends on μ." },
      { text: "15 m/s", correct: false, why: "No: √(0.25) = 0.5 exactly → 10 m/s." },
    ],
    lab: { r: 80, mu: 0.125, vCorrect: 10, vWrongDefault: 20 },
    wrongSpeeds: {},
  },
  // ── v² scaling ────────────────────────────────────────────────
  {
    id: "Q4", topic: "v² scaling",
    question: "You double your speed entering the same curve. The centripetal force needed becomes…",
    options: [
      { text: "4× as large", correct: true, why: "F = mv²/r — 2× speed → 4× force." },
      { text: "2× as large", correct: false, why: "It's v², not v: doubling speed quadruples the force." },
      { text: "The same", correct: false, why: "Your needed force mv²/r grows with the square of YOUR speed." },
      { text: "8× as large", correct: false, why: "That's v³. Centripetal force goes as v²." },
    ],
    lab: { r: 50, mu: 0.5, vCorrect: 14, vWrongDefault: 28 },
    wrongSpeeds: {},
  },
  {
    id: "Q5", topic: "v² scaling",
    question: "A curve is safe at 15 m/s with 50% of grip used. Roughly what speed uses 100% of grip?",
    options: [
      { text: "≈ 21 m/s (15·√2)", correct: true, why: "Grip used ∝ v². Doubling grip use needs √2× the speed: 15×1.41 ≈ 21 m/s." },
      { text: "30 m/s (double)", correct: false, why: "Doubling speed would use 4× the grip — 200%, a crash. √2× is the answer." },
      { text: "22.5 m/s (1.5×)", correct: false, why: "1.5² = 2.25× grip — slightly over. Exactly √2 ≈ 1.41× reaches 100%." },
      { text: "17 m/s", correct: false, why: "(17/15)² ≈ 1.28 — only 64% of grip. You can go up to 15√2 ≈ 21 m/s." },
    ],
    lab: { r: 45, mu: 0.5, vCorrect: 15, vWrongDefault: 30 },
    wrongSpeeds: {},
  },
  {
    id: "Q6", topic: "v² scaling",
    question: "Halving your speed into a curve changes the needed centripetal force to…",
    options: [
      { text: "One quarter", correct: true, why: "(½)² = ¼ — slowing down a little buys a LOT of safety." },
      { text: "One half", correct: false, why: "v is squared: (½)² = ¼." },
      { text: "Twice", correct: false, why: "Slower needs LESS inward force, not more: ¼ of it." },
      { text: "Unchanged", correct: false, why: "F = mv²/r depends strongly on v: it becomes ¼." },
    ],
    lab: { r: 60, mu: 0.4, vCorrect: 12, vWrongDefault: 24 },
    wrongSpeeds: {},
  },
  // ── Mass independence ─────────────────────────────────────────
  {
    id: "Q7", topic: "Mass independence",
    question: "A loaded cart (400 kg) replaces an empty one (200 kg) on the same curve. The maximum safe speed…",
    options: [
      { text: "Stays exactly the same", correct: true, why: "Needed (mv²/r) and available (μmg) both scale with m — mass cancels." },
      { text: "Halves", correct: false, why: "Friction also doubles with weight; the effects cancel exactly." },
      { text: "Doubles", correct: false, why: "More grip but proportionally more force needed: m cancels." },
      { text: "Increases by √2", correct: false, why: "No √m anywhere: v_max = √(μ·g·r)." },
    ],
    lab: { r: 40, mu: 0.4, vCorrect: 12.6, vWrongDefault: 18 },
    wrongSpeeds: {},
  },
  {
    id: "Q8", topic: "Mass independence",
    question: "Two identical curves; a bicycle (100 kg) and a truck (10 000 kg) both take them as fast as physics allows. Who can corner faster?",
    options: [
      { text: "Both have the same v_max", correct: true, why: "v_max = √(μ·g·r) — no mass in it. (Real trucks tip over — a different physics: torque!)" },
      { text: "The bicycle — lighter turns easier", correct: false, why: "Lighter needs less force but also HAS less friction — exactly proportionally." },
      { text: "The truck — more weight, more grip", correct: false, why: "More grip but more force needed in the same ratio. m cancels." },
      { text: "Cannot be compared", correct: false, why: "It can: v_max = √(μ·g·r) for both." },
    ],
    lab: { r: 50, mu: 0.6, vCorrect: 17.3, vWrongDefault: 25 },
    wrongSpeeds: {},
  },
  // ── Radius scaling ────────────────────────────────────────────
  {
    id: "Q9", topic: "Radius scaling",
    question: "In the game, Curve 1 had r ≈ 38.6 m and v_max ≈ 19.6 m/s. If r doubled to ≈ 77 m, the new v_max would be about…",
    options: [
      { text: "≈ 27.7 m/s (×√2)", correct: true, why: "v_max ∝ √r — double r gives ×√2 ≈ 1.41." },
      { text: "≈ 39.2 m/s (×2)", correct: false, why: "v_max grows as √r, not r." },
      { text: "≈ 19.6 m/s (unchanged)", correct: false, why: "A gentler curve allows MORE speed: r is in the formula." },
      { text: "≈ 9.8 m/s (halved)", correct: false, why: "Larger radius = gentler = FASTER safe speed." },
    ],
    lab: { r: 77, mu: 1.0, vCorrect: 27.7, vWrongDefault: 39 },
    wrongSpeeds: { "≈ 39.2 m/s (×2)": 39 },
  },
  {
    id: "Q10", topic: "Radius scaling",
    question: "To double a road's safe speed without changing the surface (same μ), its curve radius must be…",
    options: [
      { text: "4× as large", correct: true, why: "v_max ∝ √r → v² ∝ r: doubling v needs 4× r. This is why highways have huge sweeping curves." },
      { text: "2× as large", correct: false, why: "2× r gives only √2 ≈ 1.41× the speed. You need 4×." },
      { text: "½ as large", correct: false, why: "Smaller radius = sharper = SLOWER, not faster." },
      { text: "√2× as large", correct: false, why: "That gives only 2^¼ ≈ 1.19× the speed." },
    ],
    lab: { r: 120, mu: 0.4, vCorrect: 21.9, vWrongDefault: 31 },
    wrongSpeeds: {},
  },
  {
    id: "Q11", topic: "Radius scaling",
    question: "Where on a winding road is the speed limit physics sets the LOWEST?",
    options: [
      { text: "At the sharpest bend (smallest r)", correct: true, why: "v_max = √(μ·g·r): smallest r → smallest v_max. That's why the game's grip meter targets each curve's minimum radius." },
      { text: "At the widest bend (largest r)", correct: false, why: "Large r is gentle — it allows the HIGHEST speed." },
      { text: "On the straights", correct: false, why: "Straights have r → ∞: no centripetal limit at all." },
      { text: "Same everywhere", correct: false, why: "The limit depends on local radius — it varies along the road." },
    ],
    lab: { r: 20, mu: 0.5, vCorrect: 10, vWrongDefault: 18 },
    wrongSpeeds: {},
  },
  // ── Direction of force / velocity ─────────────────────────────
  {
    id: "Q12", topic: "Force direction",
    question: "A cart moves at CONSTANT speed around a circle. The net force on it points…",
    options: [
      { text: "Toward the centre", correct: true, why: "Direction changes continuously → inward (centripetal) acceleration → net inward force." },
      { text: "Outward", correct: false, why: "No outward force acts on the cart — 'centrifugal' is a feeling, not a force in the ground frame." },
      { text: "Forward, along the motion", correct: false, why: "Constant speed = no net tangential force. The net force is purely inward." },
      { text: "Zero", correct: false, why: "Velocity DIRECTION changes, so there IS acceleration — pointing inward." },
    ],
    lab: { r: 45, mu: 0.45, vCorrect: 13, vWrongDefault: 20 },
    wrongSpeeds: {},
  },
  {
    id: "Q13", topic: "Force direction",
    question: "The string of a whirling stone suddenly breaks. The stone flies…",
    options: [
      { text: "Along the tangent — straight in its current direction", correct: true, why: "With no inward force, Newton's 1st law takes over: straight-line motion along the tangent. Exactly how the cart leaves the track in the game." },
      { text: "Straight outward along the radius", correct: false, why: "It keeps its current (tangential) velocity — it does not shoot outward radially." },
      { text: "It spirals outward", correct: false, why: "Spirals need a continuing force; with none, the path is a straight tangent line." },
      { text: "It stops", correct: false, why: "No force is needed to keep moving — only to CHANGE motion." },
    ],
    lab: { r: 40, mu: 0.3, vCorrect: 10.9, vWrongDefault: 22 },
    wrongSpeeds: {},
  },
  {
    id: "Q14", topic: "Force direction",
    question: "In uniform circular motion, which pair is ALWAYS perpendicular?",
    options: [
      { text: "Velocity and acceleration", correct: true, why: "v is tangential, a is centripetal (radial) — always 90° apart; that's why speed stays constant while direction turns." },
      { text: "Velocity and the path", correct: false, why: "Velocity is tangent TO the path — parallel, not perpendicular." },
      { text: "Acceleration and the radius", correct: false, why: "Centripetal acceleration points ALONG the radius (inward)." },
      { text: "None — they vary", correct: false, why: "v ⊥ a holds at every instant of uniform circular motion." },
    ],
    lab: { r: 50, mu: 0.5, vCorrect: 15, vWrongDefault: 24 },
    wrongSpeeds: {},
  },
  // ── Wet roads / friction ──────────────────────────────────────
  {
    id: "Q15", topic: "Friction & rain",
    question: "Rain halves μ on a flat curve. v_max becomes…",
    options: [
      { text: "≈ 0.71× (÷√2)", correct: true, why: "v_max ∝ √μ: half μ → ×1/√2 ≈ 0.71. A 20 m/s curve becomes a 14 m/s curve." },
      { text: "Half", correct: false, why: "μ is under the root: ×0.71, not ×0.5." },
      { text: "Unchanged", correct: false, why: "On a flat curve friction IS the centripetal supply." },
      { text: "A quarter", correct: false, why: "That would need μ²." },
    ],
    lab: { r: 60, mu: 0.2, vCorrect: 11, vWrongDefault: 15.5 },
    wrongSpeeds: {},
  },
  {
    id: "Q16", topic: "Friction & rain",
    question: "On a flat curve, which force actually supplies the centripetal force?",
    options: [
      { text: "Static friction from the road, pointing toward the centre", correct: true, why: "The tyres don't slide; STATIC friction acts sideways, toward the centre — it IS the centripetal force here." },
      { text: "The engine's forward thrust", correct: false, why: "Thrust is tangential — it changes speed, not direction." },
      { text: "The normal force", correct: false, why: "On a FLAT road N is vertical — it can't point toward the centre. (On a BANKED road it can!)" },
      { text: "Gravity", correct: false, why: "Gravity is vertical; the centre is sideways. Friction does the turning." },
    ],
    lab: { r: 55, mu: 0.55, vCorrect: 17.4, vWrongDefault: 26 },
    wrongSpeeds: {},
  },
  {
    id: "Q17", topic: "Friction & rain",
    question: "Driving the game's Curve 2 (v_max = 18.4 m/s) at exactly 18.4 m/s in the dry. It starts raining mid-curve (μ halves). What happens?",
    options: [
      { text: "The cart slides off — needed grip now exceeds available", correct: true, why: "You were at 100% of grip; halving μ halves the available force while you still need the same mv²/r → slide. Always keep a margin!" },
      { text: "Nothing — v_max was already satisfied", correct: false, why: "v_max CHANGED when μ dropped: new v_max = 18.4/√2 ≈ 13 m/s. You're now over it." },
      { text: "The cart speeds up", correct: false, why: "Less friction doesn't add energy; it removes the sideways grip that was turning you." },
      { text: "The cart automatically slows to the new limit", correct: false, why: "Physics doesn't brake for you — that's the driver's job. The cart slides out." },
    ],
    lab: { r: 68, mu: 0.25, vCorrect: 13, vWrongDefault: 18.4 },
    wrongSpeeds: {},
  },
  // ── Centripetal acceleration computation ──────────────────────
  {
    id: "Q18", topic: "a_c computation",
    question: "v = 20 m/s, r = 50 m. Centripetal acceleration?",
    options: [
      { text: "8 m/s²", correct: true, why: "a_c = v²/r = 400/50 = 8 m/s²." },
      { text: "0.4 m/s²", correct: false, why: "0.4 = v/r — the formula squares v first." },
      { text: "2.5 m/s²", correct: false, why: "2.5 = r/v — inverted. a_c = v²/r." },
      { text: "400 m/s²", correct: false, why: "400 = v². Divide by r: 8 m/s²." },
    ],
    lab: { r: 50, mu: 0.85, vCorrect: 20, vWrongDefault: 30 },
    wrongSpeeds: {},
  },
  {
    id: "Q19", topic: "a_c computation",
    question: "A cart needs a_c = 5 m/s² on a r = 80 m curve. What speed is it doing?",
    options: [
      { text: "20 m/s", correct: true, why: "v = √(a_c·r) = √400 = 20 m/s." },
      { text: "400 m/s", correct: false, why: "400 is v² — root it." },
      { text: "16 m/s", correct: false, why: "16 = 80/5 = r/a_c — wrong rearrangement. v = √(a_c·r)." },
      { text: "0.0625 m/s", correct: false, why: "That's a_c/r. Rearrange a_c = v²/r → v = √(a_c·r) = 20." },
    ],
    lab: { r: 80, mu: 0.5, vCorrect: 20, vWrongDefault: 28 },
    wrongSpeeds: {},
  },
  {
    id: "Q20", topic: "a_c computation",
    question: "Same speed, two curves: r = 25 m and r = 100 m. The a_c on the sharp curve is…",
    options: [
      { text: "4× the gentle one", correct: true, why: "a_c = v²/r — quarter the radius, four times the acceleration." },
      { text: "¼ of the gentle one", correct: false, why: "Inverse relation: SMALLER r → BIGGER a_c." },
      { text: "2× the gentle one", correct: false, why: "r is not under a square root in a_c = v²/r: 100/25 = 4×." },
      { text: "The same", correct: false, why: "a_c depends on r — sharp curves demand more." },
    ],
    lab: { r: 25, mu: 0.45, vCorrect: 10.6, vWrongDefault: 18 },
    wrongSpeeds: {},
  },
  // ── Hilltop / crest ───────────────────────────────────────────
  {
    id: "Q21", topic: "Hilltop physics",
    question: "A cart crosses a hilltop (crest radius r) faster and faster. At the crest…",
    options: [
      { text: "N decreases with speed, reaching 0 at v = √(g·r)", correct: true, why: "mg − N = mv²/r → N = m(g − v²/r): faster → lighter, then lift-off." },
      { text: "N always equals mg", correct: false, why: "Part of gravity bends the path: N = m(g − v²/r) < mg." },
      { text: "N increases with speed", correct: false, why: "At a CREST the track pushes LESS as you go faster." },
      { text: "The cart can never leave a smooth track", correct: false, why: "Once v² > g·r, gravity can't bend the path enough — lift-off." },
    ],
    lab: { r: 40, mu: 1.0, vCorrect: 19, vWrongDefault: 28 },
    wrongSpeeds: {},
  },
  {
    id: "Q22", topic: "Hilltop physics",
    question: "At the exact moment a cart lifts off a crest, a passenger feels…",
    options: [
      { text: "Weightless — N = 0, only gravity acts", correct: true, why: "Apparent weight IS the normal force. N = 0 → the 'stomach drop' of a coaster: free fall while still moving forward." },
      { text: "Heavier than usual", correct: false, why: "Heavier happens at the BOTTOM of dips (N > mg). At a crest you feel lighter." },
      { text: "Pushed outward by centrifugal force", correct: false, why: "What you feel is the ABSENCE of support (N→0), not a real outward force." },
      { text: "Normal weight", correct: false, why: "N has dropped to zero — apparent weight is zero." },
    ],
    lab: { r: 45, mu: 1.0, vCorrect: 21, vWrongDefault: 30 },
    wrongSpeeds: {},
  },
  {
    id: "Q23", topic: "Hilltop physics",
    question: "A road has a crest of radius 90 m (g = 10). Above what speed do wheels leave the road?",
    options: [
      { text: "30 m/s", correct: true, why: "v = √(g·r) = √900 = 30 m/s — above this, N would have to be negative." },
      { text: "900 m/s", correct: false, why: "900 = v². Square root: 30 m/s." },
      { text: "9 m/s", correct: false, why: "9 = r/g — wrong combination. v = √(g·r) = 30." },
      { text: "Wheels never leave a road", correct: false, why: "They do — exactly when v² > g·r. Crest warning signs exist for this." },
    ],
    lab: { r: 90, mu: 1.0, vCorrect: 30, vWrongDefault: 38 },
    wrongSpeeds: {},
  },
  // ── Vertical circle / loop ────────────────────────────────────
  {
    id: "Q24", topic: "Vertical loop",
    question: "Minimum speed AT THE TOP of a vertical loop of radius r for the cart to keep contact?",
    options: [
      { text: "√(g·r) — gravity alone supplies the centripetal force", correct: true, why: "At the top, mg (plus N) points to the centre. Minimum: N = 0 → v_top = √(g·r)." },
      { text: "Zero — the track holds it", correct: false, why: "The track can only PUSH (N ≥ 0). Below √(g·r), gravity exceeds the needed force and the cart falls away." },
      { text: "√(2·g·r)", correct: false, why: "That's the speed from falling a height r — unrelated. Top condition: v² ≥ g·r." },
      { text: "√(5·g·r)", correct: false, why: "√(5gr) is the minimum at the BOTTOM (energy carries it to the top with √(gr) left)." },
    ],
    lab: { vertical: true, r: 30, vCorrect: 27.4, vWrongDefault: 20 },
    wrongSpeeds: {},
  },
  {
    id: "Q25", topic: "Vertical loop",
    question: "Minimum ENTRY speed at the bottom of a frictionless loop of radius r?",
    options: [
      { text: "√(5·g·r)", correct: true, why: "Need v_top² = g·r; climbing 2r costs 4gr (energy): v_bottom² = gr + 4gr = 5gr." },
      { text: "√(g·r)", correct: false, why: "That's needed AT THE TOP. The bottom needs extra to climb 2r: √(5gr)." },
      { text: "√(2·g·r)", correct: false, why: "That only just reaches height r with zero speed — not even the top, let alone with √(gr) remaining." },
      { text: "Any speed — the track guides it", correct: false, why: "Below √(5gr) the cart leaves the track partway up (N → 0) and becomes a projectile." },
    ],
    lab: { vertical: true, r: 30, vCorrect: 38.7, vWrongDefault: 24.5 },
    wrongSpeeds: { "√(g·r)": 17.3, "√(2·g·r)": 24.5 },
  },
  {
    id: "Q26", topic: "Vertical loop",
    question: "Where in a vertical loop is the normal force (and your apparent weight) the GREATEST?",
    options: [
      { text: "At the bottom — N = m(g + v²/r)", correct: true, why: "At the bottom N must beat gravity AND bend the path upward: N = mg + mv²/r. That's the crushing 'heavy' feeling." },
      { text: "At the top", correct: false, why: "Top is the LIGHTEST point: N = m(v²/r − g), possibly zero." },
      { text: "At the sides", correct: false, why: "At the sides N = mv²/r only — between the extremes." },
      { text: "Same everywhere", correct: false, why: "N varies dramatically — bottom max, top min. That's the thrill profile of a loop." },
    ],
    lab: { vertical: true, r: 25, vCorrect: 35.4, vWrongDefault: 22 },
    wrongSpeeds: {},
  },
  // ── Banking (concept preview) ─────────────────────────────────
  {
    id: "Q27", topic: "Banked curves",
    question: "Why are racing tracks and highways BANKED (tilted inward) at curves?",
    options: [
      { text: "The tilted normal force gains an inward component — turning needs less friction", correct: true, why: "On a banked road, N·sinθ points toward the centre, sharing the centripetal job. At the 'design speed', no friction is needed at all." },
      { text: "To drain rainwater only", correct: false, why: "Drainage is a side benefit; the physics reason is the inward component of N." },
      { text: "To make cars slow down", correct: false, why: "Banking lets cars go FASTER safely, not slower." },
      { text: "To reduce the curve radius", correct: false, why: "Banking doesn't change r — it changes which forces supply mv²/r." },
    ],
    lab: { r: 70, mu: 0.3, vCorrect: 14.5, vWrongDefault: 22 },
    wrongSpeeds: {},
  },
  // ── Game-integrated reasoning ─────────────────────────────────
  {
    id: "Q28", topic: "Game strategy",
    question: "In the game you score most by crossing a curve at 85–100% of v_max. The physics lesson behind that scoring is…",
    options: [
      { text: "Engineering uses calculated limits with a small margin — not guesswork, not fear", correct: true, why: "Real design (roads, coasters, aircraft) computes the limit, then operates just under it. Too timid wastes capability; over the limit is failure." },
      { text: "Faster is always better", correct: false, why: "Over v_max is a derail — the scoring punishes exceeding the limit." },
      { text: "Slower is always safer, so slowest wins", correct: false, why: "Crawling scores least: if physics says 19 m/s is safe, 18 IS safe — trust the calculation." },
      { text: "Scoring is arbitrary", correct: false, why: "It mirrors engineering practice: operate near, never over, computed limits." },
    ],
    lab: { r: 38, mu: 1.0, vCorrect: 19.5, vWrongDefault: 25 },
    wrongSpeeds: {},
  },
  {
    id: "Q29", topic: "Game strategy",
    question: "Your cart derails in the game while the speedometer still shows a constant 25 m/s. How can you crash at constant speed?",
    options: [
      { text: "Velocity DIRECTION was changing — the needed inward force exceeded supply", correct: true, why: "Constant speed ≠ constant velocity. Turning at 25 m/s needs mv²/r inward; when the track can't supply it, you go straight — off the curve." },
      { text: "Impossible — constant speed cannot crash", correct: false, why: "Direction change requires force too. Running out of INWARD force derails you at any constant speed." },
      { text: "The game has a bug", correct: false, why: "It's physics: a_c = v²/r needs force even at constant speed." },
      { text: "Speed sensors fail in curves", correct: false, why: "The speed reading was fine; the curve demanded more centripetal force than available." },
    ],
    lab: { r: 35, mu: 0.5, vCorrect: 13.2, vWrongDefault: 25 },
    wrongSpeeds: {},
  },
  {
    id: "Q30", topic: "Period & frequency",
    question: "A cart circles a r = 50 m track at 10 m/s. Time for one full lap?",
    options: [
      { text: "≈ 31.4 s", correct: true, why: "T = 2πr/v = 2π·50/10 = 10π ≈ 31.4 s." },
      { text: "5 s", correct: false, why: "5 = r/v — that ignores the 2π: circumference is 2πr ≈ 314 m." },
      { text: "≈ 314 s", correct: false, why: "314 is the circumference in metres; divide by v: 31.4 s." },
      { text: "≈ 62.8 s", correct: false, why: "That's 2T — check: 2π·50/10 = 31.4 s." },
    ],
    lab: { r: 50, mu: 0.5, vCorrect: 10, vWrongDefault: 10 },
    wrongSpeeds: {},
  },
];

// ─── PHASE 2 EXAM: SUVAT & Energy (drawn separately) ─────────────
export const PHASE2_QUESTIONS = [
  {
    id: "S1", topic: "Braking distance",
    question: "A cart at 20 m/s brakes at 10 m/s². Minimum stopping distance?",
    options: [
      { text: "20 m", correct: true, why: "d = v²/(2a) = 400/20 = 20 m." },
      { text: "2 m", correct: false, why: "2 = v/a — that's the stopping TIME (s), not distance. d = v²/(2a) = 20 m." },
      { text: "40 m", correct: false, why: "40 = v²/a — missing the factor 2: d = v²/(2a) = 20 m." },
      { text: "200 m", correct: false, why: "200 = v²/2 — you dropped a. Divide by 2a = 20: d = 20 m." },
    ],
    lab: { suvat: true, kind: "stopdist", u: 20, a: -10, s: 200, vCorrect: 20 },
  },
  {
    id: "S2", topic: "Braking distance scaling",
    question: "Doubling your speed multiplies the braking distance by…",
    options: [
      { text: "4", correct: true, why: "d ∝ v². This single fact is most of road-safety physics." },
      { text: "2", correct: false, why: "d = v²/(2a): speed is squared — 2× speed → 4× distance." },
      { text: "√2", correct: false, why: "That's backwards: d grows with v², not √v." },
      { text: "8", correct: false, why: "8 would be v³. It's v²: 4×." },
    ],
    lab: null,
  },
  {
    id: "S3", topic: "SUVAT equation choice",
    question: "Known: u, a, distance s. Wanted: final v. NO time given. Which equation?",
    options: [
      { text: "v² = u² + 2·a·s", correct: true, why: "The t-free SUVAT equation — match the equation to the knowns." },
      { text: "v = u + a·t", correct: false, why: "Contains t, which you don't have. Use v² = u² + 2as." },
      { text: "s = u·t + ½·a·t²", correct: false, why: "Two unknowns then (v missing, t unknown). Use v² = u² + 2as." },
      { text: "s = (u + v)·t / 2", correct: false, why: "Needs t. The t-free choice is v² = u² + 2as." },
    ],
    lab: null,
  },
  {
    id: "S4", topic: "Free fall",
    question: "A cart drops from the track, 20 m above water (g = 10, no drag). Impact speed?",
    options: [
      { text: "20 m/s", correct: true, why: "v = √(2gh) = √400 = 20 m/s — energy: mgh → ½mv²." },
      { text: "400 m/s", correct: false, why: "400 = v². Root it: 20 m/s." },
      { text: "10 m/s", correct: false, why: "That ignores the height doubling under the root: √(2·10·20) = 20." },
      { text: "200 m/s", correct: false, why: "200 = g·h. v = √(2gh) = 20 m/s." },
    ],
    lab: { suvat: true, kind: "accel", u: 0, a: 10, s: 20, vCorrect: 20 },
  },
  {
    id: "S5", topic: "Energy conservation",
    question: "Engine off, the cart climbs a 5 m hill. Minimum entry speed (g = 10)?",
    options: [
      { text: "10 m/s", correct: true, why: "½v² = gΔh → v = √(2·10·5) = 10 m/s — exactly the game's Energy Gate." },
      { text: "5 m/s", correct: false, why: "½·25 = 12.5 J/kg < gΔh = 50 J/kg — rolls back below half-height. Need √(2gΔh) = 10." },
      { text: "50 m/s", correct: false, why: "50 = gΔh, units m²/s². Root of 2gΔh: 10 m/s." },
      { text: "Depends on the cart's mass", correct: false, why: "½mv² = mgΔh — m cancels. Same speed for every cart." },
    ],
    lab: { suvat: true, kind: "launchspeed", u: 5, a: -10, s: 5, vCorrect: 10 },
  },
  {
    id: "S6", topic: "Energy bookkeeping",
    question: "A 200 kg cart at 12 m/s coasts (engine off) up a hill and arrives at the top at 2 m/s. Height climbed (no friction, g = 10)?",
    options: [
      { text: "7 m", correct: true, why: "Δh = (v₁² − v₂²)/(2g) = (144 − 4)/20 = 7 m." },
      { text: "14 m", correct: false, why: "Missing the ½: ΔKE = ½m(v₁²−v₂²) → Δh = (v₁²−v₂²)/(2g) = 7 m." },
      { text: "7.2 m", correct: false, why: "Close, but don't ignore the final KE: (144 − 4)/20 = 7.0 m." },
      { text: "Depends on the slope's shape", correct: false, why: "Energy conservation doesn't care about the path — only Δh. That's its power." },
    ],
    lab: null,
  },
  {
    id: "S7", topic: "Work and power",
    question: "Your engine applies 1600 N at a steady 10 m/s for 5 s. Work done?",
    options: [
      { text: "80 kJ", correct: true, why: "W = F·d = 1600 × (10·5) = 80 000 J. (Power = F·v = 16 kW.)" },
      { text: "16 kJ", correct: false, why: "16 000 = F·v — that's POWER (W), not work. Multiply by time: 80 kJ." },
      { text: "8 kJ", correct: false, why: "Distance is v·t = 50 m, so W = 1600·50 = 80 kJ." },
      { text: "1600 J", correct: false, why: "That's the force value, not work. W = F·d = 80 kJ." },
    ],
    lab: null,
  },
  {
    id: "S8", topic: "v-t graphs",
    question: "On a v-t graph of your braking, the AREA under the line represents…",
    options: [
      { text: "The distance travelled while stopping", correct: true, why: "Area under v-t = displacement. A braking triangle: ½·v·t = the braking distance." },
      { text: "The deceleration", correct: false, why: "Deceleration is the SLOPE of the v-t line, not the area." },
      { text: "The kinetic energy", correct: false, why: "KE relates to v², not to the graph's area. Area under v-t is distance." },
      { text: "Nothing physical", correct: false, why: "It's one of the most useful facts in kinematics: area = displacement." },
    ],
    lab: null,
  },
  {
    id: "S9", topic: "Stopping = energy view",
    question: "Braking from speed v to rest, the kinetic energy ½mv² goes…",
    options: [
      { text: "Into heat in the brakes/track (friction work)", correct: true, why: "Energy is conserved overall — braking converts ordered KE into thermal energy. That's also the 'wear' you minimized in Challenge mode." },
      { text: "It disappears — stopping destroys energy", correct: false, why: "Energy never disappears. It becomes heat: F_brake × d = ½mv²." },
      { text: "Back into the battery automatically", correct: false, why: "Only with regenerative braking (electric trains/cars). Friction brakes make heat." },
      { text: "Into potential energy", correct: false, why: "Only if you climb. On a flat brake zone, it's heat." },
    ],
    lab: null,
  },
  {
    id: "S10", topic: "Connecting the phases",
    question: "Phase 1 curves and Phase 2 braking share one deep idea. Which?",
    options: [
      { text: "Both limits go as v² — speed is quadratically expensive everywhere", correct: true, why: "Curve demand: mv²/r. Braking distance: v²/(2a). Halve your speed and both dangers fall by 4×." },
      { text: "Both depend on the cart's mass", correct: false, why: "Mass cancels in both! v_max = √(g·r·cosθ) and d = v²/(2a) are mass-free." },
      { text: "Both are about friction only", correct: false, why: "The deeper link is the v² — the energy/force cost of speed grows with its square." },
      { text: "Nothing — they're separate topics", correct: false, why: "Physics is connected: v² appears in KE (½mv²), curves (v²/r) and braking (v²/2a) for the same reason." },
    ],
    lab: null,
  },
];

const BANKS = { P1: EXAM_QUESTIONS, P2: PHASE2_QUESTIONS };

/** Draw n random questions for a phase — a fresh set each attempt. */
export function drawExamSet(n = 8, phase = "P1") {
  const a = [...(BANKS[phase] || EXAM_QUESTIONS)];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, Math.min(n, a.length)).map((q) => q.id);
}

export function questionById(id) {
  return (
    EXAM_QUESTIONS.find((q) => q.id === id) ||
    PHASE2_QUESTIONS.find((q) => q.id === id)
  );
}

/** Speed the lab should drive to demonstrate the student's wrong pick. */
export function wrongSpeedFor(q, optionText) {
  if (q.wrongSpeeds && q.wrongSpeeds[optionText] != null) return q.wrongSpeeds[optionText];
  // For SUVAT questions the option text starts with the numeric answer, e.g. "20 m" or "5 m/s"
  if (q.lab?.suvat) {
    const num = parseFloat(optionText);
    if (!isNaN(num)) return num;
  }
  return q.lab?.vWrongDefault ?? 10;
}
